import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { SaleTax } from "@/types/system-setting/sale-tax";

import { useDeleteSalesTaxMutation } from "@/services/salesTaxApi";
import warning from "@/assets/warning.svg";


export function DataTableRowActions({
  data,
  refetch,
  handleEdit,
  access,
}: {
  data: SaleTax;
  refetch: () => void;
  handleEdit: (payload: SaleTax) => void;
  access: string;
}) {
  const [isdelete, setIsDelete] = React.useState(false);
  const [deleteSalesTax, { isLoading: deleteLoading }] = useDeleteSalesTaxMutation();
  const { toast } = useToast();

  const deleteRow = async () => {
    try {
      const resp = await deleteSalesTax(data.id as number).unwrap();
      await refetch();
      toast({ variant: "success", title: "Tax/VAT Deleted Successfully" });
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError?.data?.detail ?? "Something went wrong.",
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-4">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(data);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>

          {access === "full_access" && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setIsDelete(true);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={setIsDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-center gap-4">
                  <img src={warning} alt="warning" className="w-18 h-18" />
                  <AlertDialogTitle className="text-xl font-semibold text-center">
                    Please confirm if you want to delete this Tax/VAT
                  </AlertDialogTitle>
                </div>
                <div className="flex justify-between gap-3 mt-4">
                  <AlertDialogCancel className="w-full border border-primary font-semibold">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow();
                    }}
                    className="w-full bg-primary text-black font-semibold"
                  >
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
